import type { Content, Editor } from "@tiptap/core";
import { Fragment, type Node } from "@tiptap/pm/model";
import { TextSelection, type EditorState } from "@tiptap/pm/state";

// Only an append caret in a terminal list qualifies. Replacements and earlier
// list positions must keep the shared card inserter's selection semantics.
export const terminalDraftListEnd = (state: EditorState): number | null => {
  const { selection, doc } = state;
  if (!(selection instanceof TextSelection) || !selection.empty || selection.from !== TextSelection.atEnd(doc).from) return null;
  const { $from } = selection;
  if ($from.depth < 2) return null;
  const outermost = $from.node(1);
  if (outermost.type.name !== "bulletList" && outermost.type.name !== "orderedList" && outermost.type.name !== "taskList") return null;
  return $from.after(1) === doc.content.size ? $from.after(1) : null;
};

// Append complete blocks in one transaction so card undo cannot leave a
// paragraph created only to get the caret out of the list.
export const buildDraftListAppendTransaction = (state: EditorState, paragraphs: Node[]) => {
  const position = terminalDraftListEnd(state);
  if (position === null || !paragraphs.length) return null;
  const content = Fragment.fromArray(paragraphs);
  if (!state.doc.canReplace(state.doc.childCount, state.doc.childCount, content)) return null;
  const tr = state.tr.insert(position, content);
  return tr.setSelection(TextSelection.create(tr.doc, position + content.size - 1));
};

export const insertDraftStreamContent = (editor: Editor, content: Content, placement: { prepared: boolean }) => {
  if (!content || (Array.isArray(content) && !content.length)) return;
  if (!placement.prepared) {
    editor.commands.focus("end");
    const { state } = editor;
    const tr = buildDraftListAppendTransaction(state, [state.schema.nodes.paragraph.create()]);
    if (tr) {
      tr.ensureMarks(state.storedMarks || state.selection.$from.marks());
      editor.view.dispatch(tr);
    }
    placement.prepared = true;
  }
  editor.commands.insertContent(content);
};
